// Einheiten, Laufzeiten und Kampfberechnung – gemeinsam für Server und Browser.
import { RESOURCES, WORLD, type BuildingId, type Cost } from "./game.ts";

export type UnitId = "pikenier" | "schwert" | "axt" | "kundschafter" | "reiter" | "ramme" | "fuerst";
export type UnitClass = "inf" | "cav";

export interface UnitDef {
  name: string;
  description: string;
  cost: Cost;
  pop: number;
  attack: number;
  defInf: number; // Verteidigung gegen Fußtruppen
  defCav: number; // Verteidigung gegen Reiter
  cls: UnitClass;
  speed: number; // Minuten pro Feld
  carry: number; // Beute-Tragkraft
  time: number; // Ausbildungszeit in Sekunden bei Gebäudestufe 1
  building: BuildingId; // wo die Einheit ausgebildet wird
  level: number; // benötigte Stufe dieses Gebäudes
}

export const UNITS: Record<UnitId, UnitDef> = {
  pikenier: {
    name: "Pikenier",
    description: "Günstiger Verteidiger, stark gegen Reiter.",
    cost: { holz: 50, lehm: 30, eisen: 10 },
    pop: 1, attack: 10, defInf: 15, defCav: 45, cls: "inf", speed: 18, carry: 25, time: 120, building: "kaserne", level: 1,
  },
  schwert: {
    name: "Schwertkämpfer",
    description: "Solider Verteidiger gegen Fußtruppen.",
    cost: { holz: 30, lehm: 30, eisen: 70 },
    pop: 1, attack: 25, defInf: 50, defCav: 15, cls: "inf", speed: 22, carry: 15, time: 160, building: "kaserne", level: 2,
  },
  axt: {
    name: "Axtkrieger",
    description: "Billige Angriffs-Infanterie.",
    cost: { holz: 60, lehm: 30, eisen: 40 },
    pop: 1, attack: 40, defInf: 10, defCav: 5, cls: "inf", speed: 18, carry: 10, time: 150, building: "kaserne", level: 3,
  },
  kundschafter: {
    name: "Kundschafter",
    description: "Späht Truppen, Rohstoffe und Gebäude aus.",
    cost: { holz: 50, lehm: 50, eisen: 20 },
    pop: 2, attack: 0, defInf: 2, defCav: 1, cls: "cav", speed: 9, carry: 0, time: 100, building: "kaserne", level: 2,
  },
  reiter: {
    name: "Reiter",
    description: "Schneller, starker Angreifer mit großer Tragkraft.",
    cost: { holz: 125, lehm: 100, eisen: 250 },
    pop: 4, attack: 130, defInf: 30, defCav: 40, cls: "cav", speed: 10, carry: 80, time: 300, building: "kaserne", level: 5,
  },
  ramme: {
    name: "Rammbock",
    description: "Reißt nach einem Sieg den Wall ein.",
    cost: { holz: 300, lehm: 200, eisen: 200 },
    pop: 5, attack: 2, defInf: 20, defCav: 50, cls: "inf", speed: 30, carry: 0, time: 480, building: "kaserne", level: 8,
  },
  fuerst: {
    name: "Fürst",
    description: "Senkt nach einem Sieg die Zustimmung. Bei 0 gehört das Dorf dir.",
    cost: { holz: 20000, lehm: 25000, eisen: 20000 },
    pop: 100, attack: 30, defInf: 100, defCav: 50, cls: "inf", speed: 35, carry: 0, time: 7200, building: "adelshof", level: 1,
  },
};

/** Fürsten werden mit jedem eigenen Dorf teurer (+50 % je weiterem Dorf). */
export function unitCost(id: UnitId, villagesOwned = 1): Cost {
  const c = UNITS[id].cost;
  const f = id === "fuerst" ? 1 + 0.5 * Math.max(0, villagesOwned - 1) : 1;
  return { holz: Math.round(c.holz * f), lehm: Math.round(c.lehm * f), eisen: Math.round(c.eisen * f) };
}

export const UNIT_IDS = Object.keys(UNITS) as UnitId[];
export type Units = Record<UnitId, number>;

export function noUnits(): Units {
  return Object.fromEntries(UNIT_IDS.map((u) => [u, 0])) as Units;
}

/** Bereinigt beliebige Eingaben zu gültigen, nicht-negativen Ganzzahlen. */
export function cleanUnits(raw: unknown): Units {
  const out = noUnits();
  if (raw && typeof raw === "object") {
    for (const u of UNIT_IDS) {
      const n = Math.floor(Number((raw as Record<string, unknown>)[u]) || 0);
      out[u] = Math.max(0, n);
    }
  }
  return out;
}

export function addUnits(a: Units, b: Partial<Units>, sign = 1): Units {
  const out = { ...a };
  for (const u of UNIT_IDS) out[u] = Math.max(0, (out[u] ?? 0) + sign * (b[u] ?? 0));
  return out;
}

export function totalUnits(u: Partial<Units>): number {
  return UNIT_IDS.reduce((s, id) => s + (u[id] ?? 0), 0);
}

export function unitPop(u: Partial<Units>): number {
  return UNIT_IDS.reduce((s, id) => s + (u[id] ?? 0) * UNITS[id].pop, 0);
}

/** Ausbildungszeit pro Einheit; jede Stufe des Ausbildungsgebäudes spart ~6 %. */
export function recruitTime(id: UnitId, buildingLevel: number): number {
  const t = UNITS[id].time * Math.pow(0.94, Math.max(0, buildingLevel - 1));
  return Math.max(1, Math.round(t / WORLD.speed));
}

export function distance(a: { x: number; y: number }, b: { x: number; y: number }): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/** Laufzeit in Sekunden – die langsamste Einheit bestimmt das Tempo. */
export function travelTime(units: Partial<Units>, dist: number): number {
  const slowest = Math.max(0, ...UNIT_IDS.filter((u) => (units[u] ?? 0) > 0).map((u) => UNITS[u].speed));
  return Math.max(1, Math.round((dist * slowest * 60) / WORLD.speed));
}

/** So lange nach dem Abschicken kann man Truppen noch zurückrufen. */
export const CANCEL_WINDOW_MS = 10 * 60_000;

export function carryCapacity(u: Partial<Units>): number {
  return UNIT_IDS.reduce((s, id) => s + (u[id] ?? 0) * UNITS[id].carry, 0);
}

// ---------- Kampf ----------

export interface BattleInput {
  attacker: Units;
  defender: Units;
  wall: number;
}

export interface BattleResult {
  attackerWon: boolean;
  attackerLosses: Units;
  defenderLosses: Units;
  scoutsSurvived: boolean; // Angreifer-Kundschafter haben überlebt → Spähbericht
  wallBefore: number;
  wallAfter: number;
}

function scaleLosses(units: Units, ratio: number): Units {
  const out = noUnits();
  for (const u of UNIT_IDS) out[u] = Math.min(units[u], Math.round(units[u] * ratio));
  return out;
}

const LOSS_EXP = 1.5;

/**
 * Vereinfachte Kampfformel:
 * 1. Kundschafter kämpfen vorab nur gegeneinander.
 * 2. Die restlichen Truppen: Angriffskraft (getrennt nach Fuß/Reiter) gegen gewichtete Verteidigung.
 *    Der Wall gibt einen Prozent-Bonus plus eine Grundverteidigung.
 * 3. Verlierer verliert alles, Sieger verliert (schwach/stark)^1.5 seiner Truppen.
 * 4. Gewinnt der Angreifer, reißen überlebende Rammböcke den Wall ein (1 Stufe je 10 Rammen).
 */
export function battle({ attacker, defender, wall }: BattleInput): BattleResult {
  const attackerLosses = noUnits();
  const defenderLosses = noUnits();

  // 1. Kundschafter-Duell
  let scoutsSurvived = false;
  const aS = attacker.kundschafter;
  const dS = defender.kundschafter;
  if (aS > 0) {
    if (aS > dS) {
      const ratio = dS > 0 ? Math.pow(dS / aS, LOSS_EXP) : 0;
      attackerLosses.kundschafter = Math.min(aS, Math.round(aS * ratio));
      defenderLosses.kundschafter = dS;
      scoutsSurvived = aS - attackerLosses.kundschafter > 0;
    } else {
      attackerLosses.kundschafter = aS;
      defenderLosses.kundschafter = Math.min(dS, Math.round(dS * Math.pow(aS / dS, LOSS_EXP)));
    }
  }

  // 2. Hauptkampf ohne Kundschafter
  const att = { ...attacker, kundschafter: 0 };
  const def = { ...defender, kundschafter: defender.kundschafter - defenderLosses.kundschafter };
  let attInf = 0;
  let attCav = 0;
  for (const u of UNIT_IDS) {
    if (UNITS[u].cls === "inf") attInf += att[u] * UNITS[u].attack;
    else attCav += att[u] * UNITS[u].attack;
  }
  const attTotal = attInf + attCav;

  let attackerWon = true;
  let wallAfter = wall;
  if (totalUnits(att) > 0) {
    let defInf = 0;
    let defCav = 0;
    for (const u of UNIT_IDS) {
      defInf += def[u] * UNITS[u].defInf;
      defCav += def[u] * UNITS[u].defCav;
    }
    const share = attTotal > 0 ? attInf / attTotal : 1;
    const defTotal = (20 + 50 * wall + defInf * share + defCav * (1 - share)) * (1 + 0.04 * wall);

    if (attTotal > defTotal) {
      const r = Math.pow(defTotal / attTotal, LOSS_EXP);
      Object.assign(attackerLosses, addUnits(scaleLosses(att, r), { kundschafter: attackerLosses.kundschafter }));
      for (const u of UNIT_IDS) if (u !== "kundschafter") defenderLosses[u] = def[u];
      const ramsLeft = att.ramme - attackerLosses.ramme;
      wallAfter = Math.max(0, wall - Math.floor(ramsLeft / 10));
    } else {
      attackerWon = false;
      for (const u of UNIT_IDS) if (u !== "kundschafter") attackerLosses[u] = att[u];
      const r = attTotal > 0 ? Math.pow(attTotal / defTotal, LOSS_EXP) : 0;
      const l = scaleLosses(def, r);
      for (const u of UNIT_IDS) if (u !== "kundschafter") defenderLosses[u] = l[u];
    }
  } else {
    // Reiner Späh-Angriff: kein Hauptkampf
    attackerWon = scoutsSurvived;
  }

  return { attackerWon, attackerLosses, defenderLosses, scoutsSurvived, wallBefore: wall, wallAfter };
}

/** Verteilt die Tragkraft möglichst gleichmäßig auf die vorhandenen Rohstoffe. */
export function plunder(available: Cost, capacity: number): Cost {
  const loot: Cost = { holz: 0, lehm: 0, eisen: 0 };
  let left = capacity;
  let open = RESOURCES.filter((r) => Math.floor(available[r]) > 0);
  while (left > 0 && open.length) {
    const share = Math.floor(left / open.length) || 1;
    for (const r of open) {
      const take = Math.min(share, Math.floor(available[r]) - loot[r], left);
      loot[r] += take;
      left -= take;
      if (left <= 0) break;
    }
    open = open.filter((r) => Math.floor(available[r]) - loot[r] > 0);
  }
  return loot;
}
